/**
 * Shared commission calculation utilities.
 * Used by GrossMarginCalculator and note_commission modal.
 *
 * Alarm center and SIM card costs are sourced from the product catalog
 * (categories: alarm_central_supplier, simcard_supplier) — not hardcoded.
 */

// ── Constants ──────────────────────────────────────────────

/** Commission rate applied to adjusted TB */
export const COMMISSION_RATE = 0.07;

/** Categories that require a SIM card subscription */
export const SIMCARD_CATEGORIES = ['alarm', 'display', 'vehicle_tracker'];

/** Categories that require alarm center subscription */
export const ALARM_CENTER_CATEGORIES = ['alarm'];

/** Setup fee per alarm device (one-time cost, deducted from TB) */
export const SETUP_FEE_PER_ALARM = 995;

/**
 * Year-by-year commission distribution percentages.
 * Key = number of contract years, value = array of percentage points per year.
 * Sum of each array always equals 100.
 */
export const YEAR_DISTRIBUTIONS = {
  1: [100],
  2: [50, 50],
  3: [50, 25, 25],
  4: [50, 25, 12.5, 12.5],
  5: [50, 25, 12.5, 6.25, 6.25],
  6: [50, 25, 12.5, 6.25, 3.125, 3.125]
};

// ── Functions ──────────────────────────────────────────────

/**
 * Calculate commission distributed by year.
 *
 * @param {number} adjustedTB  - TB after cost deductions (can be negative → 0 commission)
 * @param {number} contractMonths - Contract length in months
 * @param {number} [commissionRate] - Commission rate (defaults to COMMISSION_RATE = 0.07)
 * @returns {{ byYear: number[], total: number }}
 */
export function calculateCommissionByYear(adjustedTB, contractMonths, commissionRate) {
  const rate = commissionRate != null ? commissionRate / 100 : COMMISSION_RATE;
  // Round up to the nearest whole year — a 38-month contract pays out over 4 years
  const years = Math.ceil(contractMonths / 12);
  const totalCommission = Math.max(0, adjustedTB) * rate;
  const commissions = [];

  const percentages = YEAR_DISTRIBUTIONS[years];

  if (percentages) {
    percentages.forEach(pct => {
      commissions.push(totalCommission * (pct / 100));
    });
  } else if (years > 6) {
    // Fallback: Y1=50%, Y2=25%, Y3=12.5%, remaining split equally
    commissions.push(totalCommission * 0.50);
    commissions.push(totalCommission * 0.25);
    commissions.push(totalCommission * 0.125);
    const remainingYears = years - 3;
    const perYear = totalCommission * 0.125 / remainingYears;
    for (let i = 0; i < remainingYears; i++) {
      commissions.push(perYear);
    }
  } else {
    // Fallback for years=0 or unexpected values: single payment
    commissions.push(totalCommission);
  }

  return {
    byYear: commissions,
    total: commissions.reduce((sum, c) => sum + c, 0)
  };
}

/**
 * Calculate cost deduction from additional costs.
 *
 * @param {number} recurringCost - Extra monthly cost (kr/month)
 * @param {number} cashCost      - Extra one-time cost (kr)
 * @param {number} contractMonths - Contract length in months
 * @returns {number}
 */
export function calculateCostDeduction(recurringCost, cashCost, contractMonths) {
  return (cashCost || 0) + ((recurringCost || 0) * contractMonths);
}

/**
 * Check if a product category requires alarm center service.
 *
 * @param {string} category
 * @returns {boolean}
 */
export function needsAlarmCenter(category) {
  return ALARM_CENTER_CATEGORIES.includes(category);
}

/**
 * Check if a product category requires a SIM card.
 *
 * @param {string} category
 * @returns {boolean}
 */
export function needsSimcard(category) {
  return SIMCARD_CATEGORIES.includes(category);
}

/**
 * Format a number as SEK currency (0 decimals).
 *
 * @param {number} value
 * @returns {string}
 */
export function formatCurrency(value) {
  if (value === null || value === undefined) return '0 kr';
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

/**
 * Format a number with Swedish locale (0 decimals, no currency symbol).
 *
 * @param {number} value
 * @returns {string}
 */
export function formatNumber(value) {
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

/**
 * Build a commission entry object for saving to the staff commissions array.
 *
 * @param {object} params
 * @param {string} params.agreementNumber
 * @param {number[]} params.commissionByYear
 * @param {number} params.commissionTotal
 * @param {number} [params.extraMonthlyCost]
 * @param {number} [params.extraCashCost]
 * @param {number} [params.totalTB]
 * @param {number} [params.adjustedTB]
 * @param {number} [params.totalRevenue]
 * @param {number} [params.totalCost]
 * @param {number} [params.hardwareCost]
 * @param {number} [params.softwareCost]
 * @param {number} [params.setupFeeCost]
 * @param {number} [params.marginPercent]
 * @returns {object}
 */
export function buildCommissionEntry({
  agreementNumber,
  customerName,
  contractPeriod,
  commissionByYear,
  commissionTotal,
  extraMonthlyCost = 0,
  extraCashCost = 0,
  totalTB,
  adjustedTB,
  totalRevenue,
  totalCost,
  hardwareCost,
  softwareCost,
  setupFeeCost,
  marginPercent,
  pdfFile,
  products,
  simcard,
  alarmCenter,
  originalAgreementNumber,
  redrawRemainingContractCost,
  redrawOriginalCommissionPaid,
  redrawTotalCost
}) {
  const entry = {
    agreement_number: agreementNumber,
    total_commission: Math.round(commissionTotal),
    extra_monthly_cost: extraMonthlyCost,
    extra_cash_cost: extraCashCost,
    is_noted: true,
    noted_at: new Date().toISOString()
  };

  if (customerName) entry.customer_name = customerName;
  if (contractPeriod != null) entry.contract_period = String(contractPeriod);

  // Optional financial details (note_commission provides these, GrossMarginCalculator does not)
  if (totalTB !== undefined) entry.total_tb = Math.round(totalTB);
  if (adjustedTB !== undefined) entry.adjusted_tb = Math.round(adjustedTB);
  if (totalRevenue !== undefined) entry.total_revenue = Math.round(totalRevenue);
  if (totalCost !== undefined) entry.total_cost = Math.round(totalCost);
  if (hardwareCost !== undefined) entry.hardware_cost = Math.round(hardwareCost);
  if (softwareCost !== undefined) entry.software_cost = Math.round(softwareCost);
  if (setupFeeCost !== undefined) entry.setup_fee_cost = Math.round(setupFeeCost);
  if (marginPercent !== undefined) entry.margin_percent = Math.round(marginPercent * 10) / 10;
  if (pdfFile) entry.pdf_file = pdfFile;
  if (products && products.length) entry.products = products;
  if (simcard) entry.simcard = simcard;
  if (alarmCenter) entry.alarm_center = alarmCenter;
  if (originalAgreementNumber) entry.original_agreement_number = originalAgreementNumber;
  if (redrawRemainingContractCost != null) entry.redraw_remaining_contract_cost = Math.round(redrawRemainingContractCost);
  if (redrawOriginalCommissionPaid != null) entry.redraw_original_commission_paid = Math.round(redrawOriginalCommissionPaid);
  if (redrawTotalCost != null) entry.redraw_total_cost = Math.round(redrawTotalCost);

  // Year-by-year breakdown
  commissionByYear.forEach((amount, index) => {
    entry[`year_${index + 1}`] = Math.round(amount);
  });

  return entry;
}
